<template>
  <div class="user-dashboard">
    <header class="dashboard-header">
      <div>
        <h1>{{ t('home.user.title') }}</h1>
        <p>{{ t('home.user.subtitle') }}</p>
      </div>
     
    </header>

    <div class="primary-actions">
   
      <v-btn
        v-if="isAdmin"
        color="#1f2937"
        variant="flat"
        size="large"
        class="primary-action-btn admin"
        @click="goToAdminAccount"
      >
        <v-icon left size="22">mdi-account-cog</v-icon>
        {{ t('home.user.actions.adminAccount') }}
      </v-btn>
    </div>

    <div
      v-if="showAdminContacts"
      class="admin-contact-card"
    >
      <div class="admin-contact-header">
        <v-icon size="24" color="#EA5545">mdi-account-group</v-icon>
        <div>
          <div class="admin-contact-title">Deine ClearmentCare Helfer</div>
          <div class="admin-contact-subtitle">Direkter Kontakt zu deinem Clearment-Team</div>
        </div>
      </div>
      <div class="admin-contact-list">
        <div
          v-for="admin in limitedAdmins"
          :key="admin.user_id"
          class="admin-contact-item"
        >
          <div class="admin-contact-avatar">
            <img
              v-if="admin.user_thumb"
              :src="`/getfile/${admin.user_thumb}`"
              alt="Admin Avatar"
            />
            <v-icon v-else size="18" color="#fff">mdi-account</v-icon>
          </div>
          <div class="admin-contact-info">
            <div class="admin-contact-name">{{ admin.user_admin_name || admin.user_name }}</div>
            <div class="admin-contact-company">{{ admin.com_name || admin.user_email }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="dashboard-grid">
      <!-- Contracts -->
      <section class="dashboard-section">
        <div class="section-header">
          <div>
            <h2>
              <v-icon size="20" color="#EA5545">mdi-shield-lock</v-icon>
              {{ t('home.user.contracts.title') }}
            </h2>
            <p>{{ t('home.user.contracts.subtitle') }}</p>
          </div>
         
        </div>
        <div v-if="isLoading.contracts" class="section-loader">
          <v-progress-circular indeterminate color="#EA5545" size="32" />
        </div>
        <div v-else-if="contracts.length > 0">
          <ContractList :contracts="contracts" />
        </div>
        <EmptyState
          v-else
          icon="mdi-file-alert"
          :title="t('home.user.contracts.emptyTitle')"
          :description="t('home.user.contracts.emptyDescription')"
        />
      </section>

      <!-- Support Tickets -->
      <section class="dashboard-section">
        <div class="section-header">
          <div>
            <h2>
              <v-icon size="20" color="#EA5545">mdi-lifebuoy</v-icon>
              {{ t('home.user.support.title') }}
            </h2>
            <p>{{ t('home.user.support.subtitle') }}</p>
          </div>
          <router-link to="/support" class="section-link">
            {{ t('home.user.actions.viewSupports') }}
            <v-icon size="16">mdi-arrow-right</v-icon>
          </router-link>
        </div>

        <div v-if="isLoading.supports" class="section-loader">
          <v-progress-circular indeterminate color="#EA5545" size="32" />
        </div>

        <div v-else-if="supports.length > 0" class="card-list">
          <div v-for="ticket in supports" :key="ticket.sup_id" class="list-card">
            <div class="card-header">
              <div>
                <p class="card-title">#{{ ticket.sup_id }} · {{ ticket.sup_title }}</p>
                <p class="card-meta">
                  {{ formatDate(ticket.sup_created_date) }}
                  <span v-if="ticket.unread_count > 0" class="badge">
                    {{ t('home.user.support.badge', { count: ticket.unread_count }) }}
                  </span>
                </p>
              </div>
              <v-chip size="small" :color="getStatusColor(ticket.sup_status)">
                {{ getSupportStatusText(ticket.sup_status) }}
              </v-chip>
            </div>
            <p class="card-preview">{{ truncate(ticket.sup_text, 140) }}</p>
            <router-link :to="`/support/${ticket.sup_id}`" class="card-link">
              {{ t('home.user.actions.openSupport') }}
              <v-icon size="16">mdi-arrow-right</v-icon>
            </router-link>
          </div>
        </div>

        <EmptyState
          v-else
          icon="mdi-email-open"
          :title="t('home.user.support.emptyTitle')"
          :description="t('home.user.support.emptyDescription')"
        />
      </section>

      <!-- Brain Requests -->
      <section class="dashboard-section">
        <div class="section-header">
          <div>
            <h2>
              <v-icon size="20" color="#EA5545">mdi-brain</v-icon>
              {{ t('home.user.brain.title') }}
            </h2>
            <p>{{ t('home.user.brain.subtitle') }}</p>
          </div>
          <router-link to="/brain" class="section-link">
            {{ t('home.user.actions.viewBrain') }}
            <v-icon size="16">mdi-arrow-right</v-icon>
          </router-link>
        </div>

        <div v-if="isLoading.brain" class="section-loader">
          <v-progress-circular indeterminate color="#EA5545" size="32" />
        </div>

        <div v-else-if="brainRequests.length > 0" class="card-list">
          <div v-for="request in brainRequests" :key="request.breq_id" class="list-card">
            <div class="card-header">
              <p class="card-title">{{ truncate(request.breq_prompt, 80) }}</p>
              <v-chip
                size="small"
                :color="request.breq_status === 'success' ? 'green' : 'orange'"
              >
                {{ request.breq_status || 'pending' }}
              </v-chip>
            </div>
            <p class="card-meta">{{ formatDate(request.breq_created_date) }}</p>
            <p class="card-preview">
              {{ request.breq_result ? truncate(request.breq_result, 160) : t('home.user.brain.noResult') }}
            </p>
          </div>
        </div>

        <EmptyState
          v-else
          icon="mdi-brain"
          :title="t('home.user.brain.emptyTitle')"
          :description="t('home.user.brain.emptyDescription')"
        />
      </section>
    </div>
  </div>
</template>

<script setup>
import { defineComponent, h, inject, onMounted, reactive, ref, computed, watch } from 'vue'
import { VIcon } from 'vuetify/components'
import { useRouter } from 'vue-router'
import { useLanguage } from '@/composables/useLanguage'
import ContractList from '@/components/objects/ContractList.vue'

const baseCalls = inject('baseCalls')
const userStore = inject('userStore')
const router = useRouter()
const { languageStore, t } = useLanguage()

const contracts = ref([])
const supports = ref([])
const brainRequests = ref([])
const userData = computed(() => userStore?.userData || null)
const isAdmin = computed(() => Number(userData.value?.user_admin || 0) === 1)
const companyAdmins = ref([])
const isLoadingAdmins = ref(false)
const adminsLoaded = ref(false)
const limitedAdmins = computed(() => companyAdmins.value.slice(0, 3))
const showAdminContacts = computed(
  () => Number(userData.value?.user_admin || 0) === 0 && limitedAdmins.value.length > 0
)
// const hasMultipleContracts = computed(() => contracts.value.length > 1)

const isLoading = reactive({
  contracts: false,
  supports: false,
  brain: false
})

function formatDate(dateString) {
  if (!dateString || dateString === '1973-01-01 00:00:00') {
    return t('common.labels.notAvailable')
  }
  const locale = languageStore?.currentLanguage?.value === 'en' ? 'en-US' : 'de-DE'
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return dateString
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function truncate(text, length) {
  if (!text) return ''
  if (text.length <= length) return text
  return `${text.substring(0, length)}…`
}

function getStatusColor(status) {
  const colors = {
    0: 'orange',
    1: 'green',
    2: 'grey',
    3: 'red'
  }
  return colors[status] || 'grey'
}

function getSupportStatusText(status) {
  const map = {
    0: 'supportIndex.status.open',
    1: 'supportIndex.status.resolved',
    2: 'supportIndex.status.closed',
    3: 'supportIndex.status.urgent'
  }
  const key = map[status] || 'supportIndex.status.unknown'
  return t(key)
}

// function goToHelp() {
//   const targetContractId = primaryContract.value?.myp_id
//   if (targetContractId) {
//     router.push({ name: 'help', params: { id: targetContractId } })
//   } else {
//     router.push({ name: 'help' })
//   }
// }

// function goToRechnung() {
//   if (!primaryContract.value) return
//   router.push({
//     name: 'rechnung',
//     params: { tarifId: primaryContract.value.myp_id }
//   })
// }

function goToAdminAccount() {
  router.push({ name: 'adminaccount' })
}

async function loadContracts() {
  isLoading.contracts = true
  try {
    const response = await baseCalls.clearmentPost({ action: 'getmyproducts' })
    if (response.data.success) {
      contracts.value = response.data.products || []
    }
  } catch (err) {
    console.error('Error loading contracts', err)
  } finally {
    isLoading.contracts = false
  }
}

async function loadSupports() {
  isLoading.supports = true
  try {
    const response = await baseCalls.clearmentPost({ action: 'getAllMySupports' })
    if (response.data.success) {
      supports.value = response.data.supports || []
    }
  } catch (err) {
    console.error('Error loading supports', err)
  } finally {
    isLoading.supports = false
  }
}

async function loadBrainRequests() {
  isLoading.brain = true
  try {
    const response = await baseCalls.clearmentPost({ action: 'getmybrainrequests' })
    if (response.data.success) {
      const requests = response.data.requests || []
      brainRequests.value = requests.slice(0, 3)
    }
  } catch (err) {
    console.error('Error loading brain requests', err)
  } finally {
    isLoading.brain = false
  }
}

async function loadCompanyAdmins() {
  if (isAdmin.value || adminsLoaded.value || isLoadingAdmins.value) return
  isLoadingAdmins.value = true
  try {
    const response = await baseCalls.userPost({ action: 'getcompanyadmins' })
    if (response.data?.success && Array.isArray(response.data.admins)) {
      companyAdmins.value = response.data.admins
      adminsLoaded.value = true
    } else {
      companyAdmins.value = []
    }
  } catch (err) {
    console.error('Error loading company admins (dashboard):', err)
    companyAdmins.value = []
  } finally {
    isLoadingAdmins.value = false
  }
}

onMounted(() => {
  loadContracts()
  loadSupports()
  loadBrainRequests()
  loadCompanyAdmins()
})

watch(userData, (newValue) => {
  if (Number(newValue?.user_admin || 0) === 0 && !adminsLoaded.value) {
    loadCompanyAdmins()
  }
  if (Number(newValue?.user_admin || 0) === 1) {
    companyAdmins.value = []
    adminsLoaded.value = false
  }
})
const EmptyState = defineComponent({
  name: 'EmptyState',
  props: {
    icon: { type: String, required: true },
    title: { type: String, required: true },
    description: { type: String, required: true }
  },
  setup(props) {
    return () =>
      h('div', { class: 'state-block', 'data-state': 'empty' }, [
        h(
          VIcon,
          { size: 36, color: '#ccc' },
          { default: () => props.icon }
        ),
        h('h4', null, props.title),
        h('p', null, props.description)
      ])
  }
})
</script>

<style scoped src="@/assets/styles/sfc/components/sites/modules/HomeUserDashboard.css"></style>
